import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/NewButton";

export type ActionSpec = {
  icon: LucideIcon;
  /** Shown in the tooltip above the button. */
  label: string;
  onClick: () => void;
  /** Default true. When false the button is omitted. */
  visible?: boolean;
  /** Red hover color for destructive actions (e.g. delete). Ignored if `className` is set. */
  danger?: boolean;
  /** Full override of the button's text/hover classes — takes precedence over the row's tone. */
  className?: string;
};

type HoverGroup = 'group-hover' | 'group-hover/header' | 'group-hover/volume';

type RowActionsProps = {
  actions: ActionSpec[];
  /** Tailwind hover-group trigger used by the parent row. Parent must have the matching
   *  `group` or `group/<name>` class. Default `group-hover/header`; use `group-hover` for
   *  plain file rows, `group-hover/volume` for volume rows, etc. */
  hoverGroup?: HoverGroup;
  /** Button color palette. `gray` (default) matches sections/emails/files; `blue` matches
   *  the volume row's blue-themed chrome. Destructive actions always switch to red on hover. */
  tone?: 'gray' | 'blue';
};

const BASE_TONE: Record<NonNullable<RowActionsProps['tone']>, { normal: string; danger: string }> = {
  gray: { normal: 'text-gray-400 hover:text-gray-600', danger: 'text-gray-400 hover:text-red-600' },
  blue: { normal: 'text-blue-400 hover:text-blue-600', danger: 'text-blue-400 hover:text-red-600' },
};

// Tailwind's JIT only picks up class names that appear as literal strings in source, so we
// can't interpolate `${hoverGroup}:opacity-100`. Map each supported hover-group to its
// literal opacity class here.
const HOVER_OPACITY: Record<HoverGroup, string> = {
  'group-hover': 'group-hover:opacity-100',
  'group-hover/header': 'group-hover/header:opacity-100',
  'group-hover/volume': 'group-hover/volume:opacity-100',
};

/** Hover-revealed action button bar shared across row components.
 *  Expects to sit inside an element whose class enables a matching `group` hover namespace. */
export const RowActions = ({ actions, hoverGroup = 'group-hover/header', tone = 'gray' }: RowActionsProps) => {
  const visible = actions.filter((a) => a.visible !== false);
  if (visible.length === 0) return null;
  const palette = BASE_TONE[tone];

  return (
    <div className={`flex items-center gap-0.5 opacity-0 ${HOVER_OPACITY[hoverGroup]} transition-opacity shrink-0`}>
      {visible.map((a) => (
        <Button
          key={a.label}
          icon={a.icon}
          variant="bare"
          size="icon-sm"
          tooltip={a.label}
          onClick={a.onClick}
          className={a.className ?? (a.danger ? palette.danger : palette.normal)}
        />
      ))}
    </div>
  );
};
